import { Badge, Skeleton } from 'antd';
import React from 'react';
import styled from 'styled-components';
import { IconWrap } from '../../icons/icon';
import { theme } from '../../theme/semanticTokens';
import { IconSize, IconVariant } from '../../types/icons/Icon.types';
import { IconType } from '../../types/icons/IconTypes.enum';

const ClickableBadge = styled.div`
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const StyledBadge = styled(Badge)<{ $count: number }>`
  & .ant-badge-count {
    line-height: 18px;
    min-height: 18px;
    min-width: 18px;
    height: 18px;
    transform: translate(55%, 0%);
    box-shadow: none;
    padding: ${({ $count }) => ($count >= 10 ? '0 4px' : '0')};
  }
`;

export enum NotificationBadgeMode {
  Action = 'action',
  Counter = 'counter'
}

export interface NotificationBadgeProps {
  /**
   * Number to display in the badge
   */
  count?: number;
  /**
   * Loading state for the badge
   */
  loading?: boolean;
  /**
   * Whether to show the badge count. When false, only the icon is displayed
   */
  showBadge?: boolean;
  /**
   * Badge display mode.
   * - 'action': shows + when count is 0 (default)
   * - 'counter': hides badge when count is 0, only shows numbers
   */
  mode?: NotificationBadgeMode;
  /**
   * Icon type to display. Defaults to Chat icon
   */
  icon?: IconType;
  /**
   * Callback fired when the badge is clicked
   */
  onClick?: () => void;
  /**
   * Optional className for styling
   */
  className?: string;
}

export const NotificationBadge: React.FC<NotificationBadgeProps> = ({
  count = 0,
  loading = false,
  showBadge = true,
  mode = NotificationBadgeMode.Action,
  icon = IconType.Chat,
  onClick,
  className = ''
}) => {
  const shouldShowBadge =
    showBadge && (mode === NotificationBadgeMode.Action || count > 0);
  const badgeCount = (mode === NotificationBadgeMode.Action) && count === 0 ? '＋' : count;

  return (
    <ClickableBadge onClick={onClick} className={className}>
      {loading ? (
        <Skeleton.Button active size="small" shape="circle" />
      ) : shouldShowBadge ? (
        <StyledBadge
          count={badgeCount}
          $count={count}
          color={theme.dime.colors.fill.infoPrimary}
        >
          <IconWrap
            type={icon}
            variant={IconVariant.Outline}
            size={IconSize.Medium}
          />
        </StyledBadge>
      ) : (
        <IconWrap
          type={icon}
          variant={IconVariant.Outline}
          size={IconSize.Medium}
        />
      )}
    </ClickableBadge>
  );
};
